import {
  ElementAddress,
  MediaElement,
  Rect,
  SlotFormat,
  SlotFrame,
  SlotLayout,
  Spread,
} from 'editor/src/store/design/types';

export interface SlotLayoutElement {
  element: MediaElement;
  address: ElementAddress;
}

/** Every layer a layout manages on the spread, in page order. */
export function getSlotLayoutElements(spread: Spread, spreadIndex: number, layoutId: string): SlotLayoutElement[] {
  const elements: SlotLayoutElement[] = [];
  spread.pages.forEach((page, pageIndex) => {
    page.groups.media?.forEach((element, elementIndex) => {
      if (element.slotFrame?.layoutId === layoutId) {
        elements.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
      }
    });
  });
  return elements;
}

export function getSlotFrameElements(spread: Spread, spreadIndex: number, layoutId: string, frameId: string) {
  return getSlotLayoutElements(spread, spreadIndex, layoutId).filter(
    ({ element }) => element.slotFrame?.frameId === frameId,
  );
}

/** A layer the author placed by hand, as opposed to a picture layer or a copy. */
export function isHandPlacedSlotElement(element: MediaElement): boolean {
  return !!element.slotFrame && !element.slotFrame.fileSetId && element.slotFrame.sourceUuid === undefined;
}

/**
 * The frame whose layers stand for every frame of the format: the first one, in product and
 * count order, that holds hand-placed layers, or the first one of the format while none does.
 * Tied to the design rather than to a position, so re-cutting the grid never turns an empty
 * new frame into the template and wipes the copies.
 */
export function getSlotTemplateFrame(
  layout: SlotLayout,
  columns: SlotFormat,
  elements: MediaElement[] = [],
): SlotFrame | undefined {
  const frames = layout.frames
    .filter((frame) => frame.columns === columns)
    .sort((a, b) => a.product - b.product || a.counts[0] - b.counts[0]);
  // Frame ids repeat across layouts, so only this layout's layers count.
  const designed = new Set(
    elements
      .filter((element) => isHandPlacedSlotElement(element) && element.slotFrame?.layoutId === layout.id)
      .map((element) => element.slotFrame!.frameId),
  );
  return frames.find((frame) => designed.has(frame.id)) ?? frames[0];
}

export function getElementBounds(element: MediaElement): Rect {
  if (element.type === 'line') {
    return {
      x: Math.min(element.x1, element.x2),
      y: Math.min(element.y1, element.y2),
      width: Math.abs(element.x2 - element.x1),
      height: Math.abs(element.y2 - element.y1),
    };
  }
  return { x: element.x, y: element.y, width: element.width, height: element.height };
}

const FRAME_TOLERANCE = 1.05;

/**
 * A loose layer belongs to a frame when it sits inside it: centred in the frame and no larger
 * than it, so a page background or a header running across the page is never swept in.
 */
export function isElementInFrame(element: MediaElement, frame: Rect): boolean {
  if ((element as { role?: string }).role) {
    return false;
  }
  const bounds = getElementBounds(element);
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  return (
    centerX >= frame.x &&
    centerX <= frame.x + frame.width &&
    centerY >= frame.y &&
    centerY <= frame.y + frame.height &&
    bounds.width <= frame.width * FRAME_TOLERANCE &&
    bounds.height <= frame.height * FRAME_TOLERANCE
  );
}
